import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminLogin, adminLogout, adminVerify } from '../utils/adminSession';
import { reportClientError } from '../utils/clientLogger';
import { PainelAdminLoginView } from '../components/admin/painel/PainelAdminLoginView';
import { PainelAdminVendedoresTab } from '../components/admin/painel/PainelAdminVendedoresTab';
import { PainelAdminLeadsTab } from '../components/admin/painel/PainelAdminLeadsTab';
import { PainelAdminProjetosTab } from '../components/admin/painel/PainelAdminProjetosTab';
import { PainelAdminMetricasTab } from '../components/admin/painel/PainelAdminMetricasTab';
import { PainelAdminVendedorModal } from '../components/admin/painel/PainelAdminVendedorModal';
import {
  painelApiClient,
  fetchPainelAdminData,
  fetchPainelAdminLeads,
  PAINEL_TABS,
  PAINEL_TAB_LABELS,
} from '../utils/painelAdminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PainelAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('vendedores');
  const [vendedores, setVendedores] = useState([]);
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddVendedor, setShowAddVendedor] = useState(false);
  const [showEditVendedor, setShowEditVendedor] = useState(false);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [vendedorForm, setVendedorForm] = useState({
    nome: '', telefone: '', whatsapp: '', email: '', especialidade: 'todos'
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await adminVerify();
      setIsAuthenticated(Boolean(response.data.valid));
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  }, [adminVerify]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await adminLogin(password);
      if (response.data.success) {
        setIsAuthenticated(true);
      }
    } catch (err) {
      setLoginError('Senha incorreta');
    }
  };

  const handleLogout = () => {
    adminLogout().finally(() => setIsAuthenticated(false));
  };

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPainelAdminData();
      setVendedores(data.vendedores);
      setMetrics(data.metrics);
      setProjects(data.projects);
    } catch (err) {
      reportClientError('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchPainelAdminData, reportClientError]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated, loadAllData]);

  const loadLeads = useCallback(async (vendedorId) => {
    try {
      const vendedorLeads = await fetchPainelAdminLeads(vendedorId);
      setLeads(vendedorLeads);
    } catch (err) {
      reportClientError('Error loading leads:', err);
    }
  }, [fetchPainelAdminLeads, reportClientError]);

  const rankedVendedores = useMemo(
    () => [...vendedores].sort((a, b) => (b.leads_count || 0) - (a.leads_count || 0)).slice(0, 5),
    [vendedores]
  );

  const handleAddVendedor = async (e) => {
    e.preventDefault();
    try {
      await painelApiClient.post(`${API}/vendedores`, vendedorForm);
      setShowAddVendedor(false);
      setVendedorForm({ nome: '', telefone: '', whatsapp: '', email: '', especialidade: 'todos' });
      loadAllData();
    } catch (err) {
      alert('Erro ao adicionar vendedor');
    }
  };

  const handleEditVendedor = async (e) => {
    e.preventDefault();
    try {
      await painelApiClient.put(`${API}/vendedores/${selectedVendedor.id}`, vendedorForm);
      setShowEditVendedor(false);
      setSelectedVendedor(null);
      loadAllData();
    } catch (err) {
      alert('Erro ao atualizar vendedor');
    }
  };

  const handleToggleAtivo = async (vendedor) => {
    try {
      await painelApiClient.put(`${API}/vendedores/${vendedor.id}`, { ativo: !vendedor.ativo });
      loadAllData();
    } catch (err) {
      alert('Erro ao atualizar status');
    }
  };

  const handleDeleteVendedor = async (vendedor) => {
    if (!window.confirm(`Remover ${vendedor.nome}?`)) return;
    try {
      await painelApiClient.delete(`${API}/vendedores/${vendedor.id}`);
      loadAllData();
    } catch (err) {
      alert('Erro ao remover');
    }
  };

  const copyLink = (codigo) => {
    navigator.clipboard.writeText(`${window.location.origin}/v/${codigo}`);
    alert('Link copiado!');
  };

  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Excluir o lead de ${lead.cliente_nome}?`)) return;

    try {
      await painelApiClient.delete(`${API}/admin/leads/${lead.id}`);
      if (selectedVendedor?.id) {
        await loadLeads(selectedVendedor.id);
      }
      await loadAllData();
    } catch (err) {
      alert('Erro ao excluir lead');
    }
  };

  const openEdit = (vendedor) => {
    setSelectedVendedor(vendedor);
    setVendedorForm({
      nome: vendedor.nome,
      telefone: vendedor.telefone || '',
      whatsapp: vendedor.whatsapp || '',
      email: vendedor.email || '',
      especialidade: vendedor.especialidade || 'todos'
    });
    setShowEditVendedor(true);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <PainelAdminLoginView
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loginError={loginError}
        handleLogin={handleLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-gray-600 hover:text-gray-900" data-testid="painel-admin-header-back-link">← Voltar ao Site</Link>
          <span className="text-blue-600 font-bold">RH Consulting Holdings</span>
          <button
            onClick={handleLogout}
            className="text-red-600 hover:text-red-700 text-sm"
            data-testid="painel-admin-logout-button"
          >
            🚪 Sair
          </button>
        </div>
      </div>

      {/* Tabs (like questionnaire steps) */}
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <div className="flex justify-between mb-2">
          {PAINEL_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-sm font-medium transition ${activeTab === tab ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              data-testid={`painel-admin-tab-${tab}`}
            >
              {PAINEL_TAB_LABELS[tab]}
            </button>
          ))}
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${((PAINEL_TABS.indexOf(activeTab) + 1) / PAINEL_TABS.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          
          {activeTab === 'vendedores' && (
            <PainelAdminVendedoresTab
              vendedores={vendedores}
              setShowAddVendedor={setShowAddVendedor}
              copyLink={copyLink}
              handleToggleAtivo={handleToggleAtivo}
              openEdit={openEdit}
              handleDeleteVendedor={handleDeleteVendedor}
            />
          )}

          {activeTab === 'leads' && (
            <PainelAdminLeadsTab
              vendedores={vendedores}
              selectedVendedor={selectedVendedor}
              setSelectedVendedor={setSelectedVendedor}
              loadLeads={loadLeads}
              leads={leads}
              handleDeleteLead={handleDeleteLead}
            />
          )}

          {activeTab === 'projetos' && <PainelAdminProjetosTab projects={projects} />}

          {activeTab === 'metricas' && (
            <PainelAdminMetricasTab metrics={metrics} projects={projects} rankedVendedores={rankedVendedores} />
          )}
        </div>
      </div>

      <PainelAdminVendedorModal
        isOpen={showAddVendedor}
        isEdit={false}
        vendedorForm={vendedorForm}
        setVendedorForm={setVendedorForm}
        onClose={() => setShowAddVendedor(false)}
        onSubmit={handleAddVendedor}
      />

      <PainelAdminVendedorModal
        isOpen={showEditVendedor}
        isEdit
        vendedorForm={vendedorForm}
        setVendedorForm={setVendedorForm}
        onClose={() => setShowEditVendedor(false)}
        onSubmit={handleEditVendedor}
      />
    </div>
  );
};

export default PainelAdmin;
